import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zonesApi } from "@/api/pricing.api";
import { queryKeys } from "@/utils/queryKeys";
import type { Zone } from "@/types/domain";

export function useZonesList() {
  return useQuery({
    queryKey: queryKeys.zones.list(),
    queryFn: () => zonesApi.list(),
  });
}

function useInvalidateZones() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.zones.list() });
}

export function useCreateZone() {
  const invalidate = useInvalidateZones();
  return useMutation({
    mutationFn: (payload: {
      name: string;
      city?: string;
      ring: number[][];
      active?: boolean;
      pricingRuleKey?: string;
    }) =>
      zonesApi.create(payload),
    onSuccess: invalidate,
  });
}

export function useUpdateZone() {
  const invalidate = useInvalidateZones();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<Zone> }) =>
      zonesApi.update(id, payload),
    onSuccess: invalidate,
  });
}

export function useSetZoneActive() {
  const invalidate = useInvalidateZones();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => zonesApi.setActive(id, active),
    onSuccess: invalidate,
  });
}

export function useDeleteZone() {
  const invalidate = useInvalidateZones();
  return useMutation({
    mutationFn: (id: string) => zonesApi.remove(id),
    onSuccess: invalidate,
  });
}

export type { Zone };
